/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import PDFViewer from './PDFViewer';
import './FileViewer.css';

const FileViewer = ({ url, onClose, fileName: knownFileName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState('unknown');
  const [fileName, setFileName] = useState(knownFileName || 'document');
  const [showFallback, setShowFallback] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageDrag, setImageDrag] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Replace port 3000 with 4000 for backend requests
  const backendUrl = url.replace(':3000', ':4000');

  // Detect file type from content type, falling back to the filename
  // extension when the server sends a generic type like application/octet-stream
  const detectFileType = (contentType, fileNameHint) => {
    // console.log('Detecting file type from content type:', contentType);

    if (contentType) {
      if (contentType.includes('image/')) {
        return 'image';
      } else if (contentType.includes('application/pdf')) {
        return 'pdf';
      }
    }

    if (fileNameHint) {
      const ext = fileNameHint.split('.').pop().toLowerCase();
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
        return 'image';
      }
      if (ext === 'pdf') {
        return 'pdf';
      }
    }

    return 'unknown';
  };

  // Fetch file data
  useEffect(() => {
    let isMounted = true;
    
    const fetchFile = async () => {
      try {
        
        // Fetch the file
        const response = await fetch(backendUrl, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Log all response headers for debugging
        
        // Get content type and disposition
        const contentType = response.headers.get('Content-Type');
        const contentDisposition = response.headers.get('Content-Disposition');
        
        
        // Extract filename if available (with or without surrounding quotes).
        // Prefer a filename the caller already knows (e.g. from a file listing)
        // since Content-Disposition isn't always exposed cross-origin by CORS.
        let extractedFileName = knownFileName || null;
        if (!extractedFileName && contentDisposition) {
          const matches = contentDisposition.match(/filename=["']?([^;"']+)["']?/i);
          if (matches && matches[1]) {
            extractedFileName = matches[1].trim();
          }
        }
        if (extractedFileName && isMounted) {
          setFileName(extractedFileName);
        }

        // Detect file type from content type
        const detectedType = detectFileType(contentType, extractedFileName);
        
        if (isMounted) {
          setFileType(detectedType);
          
          // For PDFs, don't try to load as data URL
          if (detectedType === 'pdf') {
            setLoading(false);
            // Auto-open PDF in new tab if that's the preference
            if (localStorage.getItem('autoOpenPdf') === 'true') {
              window.open(backendUrl, '_blank');
              onClose();
            } else {
              const blob = await response.blob();
              const reader = new FileReader();
              
              reader.onload = () => {
                if (isMounted) {
                  const base64Data = reader.result.split(',')[1];
                  setFileData(base64Data);
                  setLoading(false);
                }
              };
              
              reader.onerror = () => {
                if (isMounted) {
                  // console.error('Error reading file as base64');
                  setShowFallback(true);
                  setLoading(false);
                }
              };
              
              reader.readAsDataURL(blob);
            }

          } else {
            // For images and other files, get as base64
            const blob = await response.blob();
            const reader = new FileReader();
            
            reader.onload = () => {
              if (isMounted) {
                setFileData(reader.result);
                setLoading(false);
              }
            };
            
            reader.onerror = () => {
              if (isMounted) {
                // console.error('Error reading file as base64');
                setShowFallback(true);
                setLoading(false);
              }
            };
            
            reader.readAsDataURL(blob);
          }
        }
      } catch (err) {
        // console.error('Failed to fetch file:', err);
        if (isMounted) {
          setError(`Failed to fetch file: ${err.message}`);
          setLoading(false);
        }
      }
    };
    
    fetchFile();
    
    return () => {
      isMounted = false;
    };
  }, [backendUrl, onClose]);

  // Image zoom controls
  const zoomIn = () => setImageZoom(prev => Math.min(prev + 0.25, 5));
  const zoomOut = () => {
    setImageZoom(prev => {
      const next = Math.max(prev - 0.25, 0.25);
      if (next <= 1) setImageDrag({ x: 0, y: 0 });
      return next;
    });
  };
  const resetZoom = () => {
    setImageZoom(1);
    setImageDrag({ x: 0, y: 0 });
  };

  // Image drag handlers
  const handleMouseDown = (e) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imageDrag.x, y: e.clientY - imageDrag.y });
      e.preventDefault();
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging) {
      setImageDrag({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };
  const handleMouseUp = () => setIsDragging(false);

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  // Print image
  const printImage = () => {
    if (!fileData) return;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print - ${fileName}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              @media print { body { margin: 0; } img { max-width: 100%; max-height: 100%; } }
            </style>
          </head>
          <body>
            <img src="${fileData}" alt="${fileName}" onload="window.print(); window.close();" />
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  // Open file in new tab
  const openFile = () => {
    window.open(backendUrl, '_blank');
    onClose();
  };

  // Download file
  const downloadFile = () => {
    if (fileData && fileType !== 'pdf') {
      const link = document.createElement('a');
      link.href = fileData;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(backendUrl, '_blank');
    }
  };

  // Set auto-open preference
  const setAutoOpenPreference = (value) => {
    localStorage.setItem('autoOpenPdf', value.toString());
  };

  // Render file icon (fallback view)
  const renderFileIcon = () => {
    if (fileType === 'pdf') {
      return (
        <div className="FileViewer-icon FileViewer-pdf-icon">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2V8H20" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 13H8" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 17H8" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 9H9H8" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="FileViewer-icon-label">PDF Document</div>
        </div>
      );
    } else if (fileType === 'image') {
      return (
        <div className="FileViewer-icon FileViewer-image-icon">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#0d6efd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="8.5" r="1.5" fill="#0d6efd"/>
            <path d="M21 15L16 10L5 21" stroke="#0d6efd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="FileViewer-icon-label">Image</div>
        </div>
      );
    } else {
      return (
        <div className="FileViewer-icon FileViewer-unknown-icon">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2V8H20" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 12V16" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 9H12.01" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="FileViewer-icon-label">Document</div>
        </div>
      );
    }
  };

  // Render PDF viewer
  const renderPdfViewer = () => {
    return (
      <div className="FileViewer-pdf-preview">
        {renderFileIcon()}
        
        <div className="FileViewer-filename">
          {fileName}
        </div>
        
        <div className="FileViewer-pdf-message">
          <p>PDF documents can be viewed in a new tab for better compatibility.</p>
        </div>
        
        <div className="FileViewer-actions">
          <button 
            className="FileViewer-btn FileViewer-view-btn"
            onClick={openFile}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path 
                d="M14 8V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M10 2H14V6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M14 2L8 8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
            View in New Tab
          </button>
          
          <button 
            className="FileViewer-btn FileViewer-download-btn"
            onClick={downloadFile}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path 
                d="M14 11V14H2V11" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M8 2V11M8 11L5 8M8 11L11 8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Download
          </button>
        </div>
        
        <div className="FileViewer-preferences">
          <label className="FileViewer-checkbox-label">
            <input 
              type="checkbox" 
              checked={localStorage.getItem('autoOpenPdf') === 'true'}
              onChange={(e) => setAutoOpenPreference(e.target.checked)}
            />
            <span>Always open PDFs in new tab</span>
          </label>
        </div>
      </div>
    );
  };

  // Render fallback view
  const renderFallback = () => {
    return (
      <div className="FileViewer-fallback">
        {renderFileIcon()}
        
        <div className="FileViewer-filename">
          {fileName}
        </div>
        
        <div className="FileViewer-actions">
          <button 
            className="FileViewer-btn FileViewer-view-btn"
            onClick={openFile}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path 
                d="M14 8V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M10 2H14V6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M14 2L8 8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
            View in New Tab
          </button>
          
          <button 
            className="FileViewer-btn FileViewer-download-btn"
            onClick={downloadFile}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path 
                d="M14 11V14H2V11" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
              <path 
                d="M8 2V11M8 11L5 8M8 11L11 8" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Download
          </button>
        </div>
        
        {!loading && fileData && fileType === 'image' && (
          <button 
            className="FileViewer-btn FileViewer-retry-btn"
            onClick={() => setShowFallback(false)}
          >
            Try Viewing Again
          </button>
        )}
      </div>
    );
  };

  // Render image viewer
  const renderImageViewer = () => {
    return (
      <div className="FileViewer-image-viewer">
        <div
          className="FileViewer-image-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          <img
            src={fileData}
            alt={fileName}
            className="FileViewer-image"
            style={{
              transform: `scale(${imageZoom}) translate(${imageDrag.x / imageZoom}px, ${imageDrag.y / imageZoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
            }}
            draggable={false}
            onError={() => {
              // console.error('Image failed to load from data URL');
              setShowFallback(true);
            }}
          />
        </div>
        <div className="FileViewer-image-controls">
          <div className="FileViewer-zoom-controls">
            <button className="FileViewer-zoom-btn" onClick={zoomOut} title="Zoom Out">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span className="FileViewer-zoom-level">{Math.round(imageZoom * 100)}%</span>
            <button className="FileViewer-zoom-btn" onClick={zoomIn} title="Zoom In">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M11 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <button className="FileViewer-zoom-btn" onClick={resetZoom} disabled={imageZoom === 1} title="Reset Zoom">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C8.5 3 5.5 5 4 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 3V8H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="FileViewer-action-controls">
            <button className="FileViewer-zoom-btn" onClick={printImage} title="Print">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="FileViewer-zoom-btn" onClick={downloadFile} title="Download">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          {/* File info hidden — fileName is a default placeholder, not the actual filename */}
        </div>
      </div>
    );
  };

  // Render content
  const renderContent = () => {
    if (loading) {
      return (
        <div className="FileViewer-loading">
          <div className="FileViewer-spinner"></div>
          <p>Loading file...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="FileViewer-error">
          <div className="FileViewer-error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <p className="FileViewer-error-message">{error}</p>
          <button 
            className="FileViewer-btn FileViewer-retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      );
    }

    // For PDFs, use the PDF.js viewer
    if (fileType === 'pdf') {
      if (showFallback) {
        return renderPdfViewer();
      }
      return (
        <PDFViewer 
          url={''} 
          fileName={fileName}
          requestBlob={fileData}

        />
      );
    }

    // Show fallback view if requested or if no file data
    if (showFallback || !fileData) {
      return renderFallback();
    }

    // Show actual content based on file type
    if (fileType === 'image') {
      return renderImageViewer();
    } else {
      // For unknown file types, show fallback
      return renderFallback();
    }
  };

  return ReactDOM.createPortal(
    <div className="FileViewer-overlay" onClick={onClose}>
      <div
        className={`FileViewer-content ${fileType === 'image' ? 'FileViewer-content-large' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="FileViewer-header">
          <h2 className="FileViewer-title">
            {loading ? 'Loading Document...' : `${fileType === 'pdf' ? 'PDF Document' : fileType === 'image' ? 'Image' : 'Document'}`}
          </h2>

          <button
            className="FileViewer-close-btn"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="FileViewer-container">
          {renderContent()}
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="FileViewer-debug">
            URL: {backendUrl}<br />
            Type: {fileType}<br />
            Filename: {fileName}<br />
            State: {loading ? 'loading' : error ? 'error' : 'ready'}<br />
            Data URL: {fileData ? 'created' : 'none'}<br />
            Fallback: {showFallback ? 'yes' : 'no'}<br />
            Auto-open PDF: {localStorage.getItem('autoOpenPdf') === 'true' ? 'yes' : 'no'}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

FileViewer.propTypes = {
  url: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  fileName: PropTypes.string,
};

export default FileViewer;
